/**
 * admin/makua/AdminReservations.js — guests and reservations (spec 05–06, V2.1 Page 5).
 * Search, filter, sort, export; open one to work on it. The dashboard's
 * Needs Attention links arrive here as ?attention=…, and ?open=<id> opens one.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import { reservationsAdminAPI, scheduledAPI, sellersAPI } from "../../services/api";
import ReservationDrawer from "./ReservationDrawer";
import StaffBooking from "./StaffBooking";
import { useLoad, Field, money, range, fmtStamp, StatusBadge, Badge, RES_STATUS, SCREENING } from "./ops";
import { useStored, SortTh } from "./kit";
import "./ops.css";

const ATTENTION = {
  expiring: "Holds expiring in 24 hours", unpaid: "Held, waiting for payment", overdue: "Balance past its due date",
  requested: "Room requests to approve", addons: "Add-on requests to answer", unplaced: "Without a room or bed",
  screening: "Arriving soon, screening not finished",
};
const EMPTY = { q: "", status: "", pathway_id: "", screening: "", seller_id: "", from: "", to: "", source: "", balance: "", attention: "" };

const screeningSummary = (list) => {
  if (!list.length) return null;
  const cleared = list.filter((s) => s === "approved" || s === "not_required").length;
  if (list.includes("declined")) return <Badge tone="red">Not cleared</Badge>;
  if (cleared === list.length) return <Badge tone="green">All cleared</Badge>;
  return <Badge tone={list.includes("in_progress") ? "amber" : ""}>{cleared} of {list.length} cleared</Badge>;
};

export default function AdminReservations() {
  const [params, setParams] = useSearchParams();
  const [saved, setSaved] = useStored("res:filters", EMPTY);
  // A link (dashboard, guests) sets the filters it names, on a clean slate; otherwise the last ones used.
  const fromLink = ["attention", "q", "pathway_id", "status"].some((k) => params.get(k));
  const [filters, setFiltersRaw] = useState(() => (fromLink
    ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).filter((k) => params.get(k)).map((k) => [k, params.get(k)])) }
    : { ...EMPTY, ...saved, attention: "" }));
  const setFilters = (f) => { setFiltersRaw(f); setSaved({ ...f, attention: "" }); };
  const [sort, setSort] = useStored("res:sort", { key: "arrive", dir: "asc" });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(() => Number(params.get("open")) || null);
  useEffect(() => {
    // The address shows what's open, so it can be shared or bookmarked.
    const p = new URLSearchParams(params);
    if (open) p.set("open", open); else p.delete("open");
    setParams(p, { replace: true });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [newRes, setNewRes] = useState(false);
  const { data: dates } = useLoad(() => scheduledAPI.list({ scope: "all" }), []);
  const { data: sellerData } = useLoad(() => sellersAPI.list().catch(() => ({ data: { sellers: [] } })), []);
  const sellers = sellerData?.sellers || [];
  const { data, raw, loading, error, reload } = useLoad(() => reservationsAdminAPI.list({ ...filters, sort: sort.key || "", dir: sort.dir, page, limit: 50 }), [JSON.stringify(filters), sort.key, sort.dir, page]);
  const setSortAndPage = (x) => { setSort(x); setPage(1); };
  const rows = data || [];
  const pg = raw?.pagination;
  const set = (k) => (e) => { setPage(1); setFilters({ ...filters, [k]: e.target.value }); };

  return (
    <AdminLayout pageTitle="Reservations" pageSubtitle="Every booking, with its guests, payments and screening." permission="reservations.view" feature="reservations">
      <div className="ops">
        {filters.attention && (
          <p className="ops-note" role="status">
            Showing: <b>{ATTENTION[filters.attention] || filters.attention}</b> (from the dashboard).{" "}
            <button type="button" className="ops-btn--link" onClick={() => { setPage(1); setFilters({ ...filters, attention: "" }); setParams({}, { replace: true }); }}>Show all reservations</button>
          </p>
        )}
        <div className="ops-bar">
          <Field label="Search"><input value={filters.q} onChange={set("q")} placeholder="Name, email or MAK-…" /></Field>
          <Field label="Status">
            <select value={filters.status} onChange={set("status")}>
              <option value="">Any</option>
              {Object.entries(RES_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Retreat">
            <select value={filters.pathway_id} onChange={set("pathway_id")}>
              <option value="">Any</option>
              {(dates || []).map((d) => <option key={d.id} value={d.id}>{d.name} · {range(d.start_date, d.end_date)}</option>)}
            </select>
          </Field>
          <Field label="Screening">
            <select value={filters.screening} onChange={set("screening")}>
              <option value="">Any</option>
              {Object.entries(SCREENING).map(([k, v]) => <option key={k} value={k}>Anyone {v.label.toLowerCase()}</option>)}
            </select>
          </Field>
          {sellers.length > 0 && (
            <Field label="Seller">
              <select value={filters.seller_id} onChange={set("seller_id")}>
                <option value="">Any</option>
                {sellers.map((sl) => <option key={sl.id} value={sl.id}>{sl.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Booked by">
            <select value={filters.source} onChange={set("source")}>
              <option value="">Anyone</option><option value="web">Guest, on the website</option><option value="admin">Staff</option><option value="seller">A seller</option>
            </select>
          </Field>
          <Field label="Balance">
            <select value={filters.balance} onChange={set("balance")}>
              <option value="">Any</option><option value="owing">Still owing</option><option value="paid">Paid in full</option><option value="overpaid">Paid more than owed</option>
            </select>
          </Field>
          <Field label="Staying from"><input type="date" value={filters.from} onChange={set("from")} /></Field>
          <Field label="to"><input type="date" value={filters.to} onChange={set("to")} /></Field>
          <span className="ops-spacer" />
          <a className="ops-btn" href={reservationsAdminAPI.csv(filters)}>Export CSV</a>
          <button className="ops-btn ops-btn--primary" onClick={() => setNewRes(true)}>New reservation</button>
        </div>

        {error && <p className="ops-error">{error}</p>}
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <SortTh k="ref" sort={sort} setSort={setSortAndPage}>Reference</SortTh>
                <SortTh k="guest" sort={sort} setSort={setSortAndPage}>Lead guest</SortTh>
                <SortTh k="retreat" sort={sort} setSort={setSortAndPage}>Retreat</SortTh>
                <SortTh k="arrive" sort={sort} setSort={setSortAndPage}>Stay</SortTh>
                <th>Room</th>
                <SortTh k="status" sort={sort} setSort={setSortAndPage}>Status</SortTh>
                <th>Screening</th>
                <SortTh k="total" sort={sort} setSort={setSortAndPage} className="num">Total</SortTh>
                <SortTh k="balance" sort={sort} setSort={setSortAndPage} className="num">Balance</SortTh>
              </tr>
            </thead>
            <tbody>
              {loading && !rows.length && <tr><td colSpan={9} className="ops-empty">Loading…</td></tr>}
              {!loading && !rows.length && <tr><td colSpan={9} className="ops-empty">No reservations match.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><button className="ops-row-btn" onClick={() => setOpen(r.id)}>{r.ref}</button><span className="ops-sub">{fmtStamp(r.created_at)} · {r.seller_name ? `seller: ${r.seller_name}` : r.source === "web" ? "website" : "staff"}</span></td>
                  <td>{r.first_name} {r.last_name}<span className="ops-sub">{r.email}</span></td>
                  <td>{r.retreat_name}<span className="ops-sub">{r.guests_count} {r.guests_count === 1 ? "guest" : "guests"}</span></td>
                  <td>{range(r.arrive_date, r.depart_date)}</td>
                  <td>{r.room_type_name || "—"}<span className="ops-sub">{r.units || (["pending_payment", "confirmed"].includes(r.status) ? "no room yet" : "")}</span></td>
                  <td><StatusBadge status={r.status} />{r.status === "pending_payment" && <span className="ops-sub">until {fmtStamp(r.hold_expires_at)}</span>}</td>
                  <td>{screeningSummary(r.screening)}</td>
                  <td className="num">{money(r.total)}</td>
                  <td className="num">{r.status === "cancelled" ? "—" : money(r.balance)}{r.balance_due_date && r.balance > 0 && <span className="ops-sub">by {r.balance_due_date}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pg && pg.totalPages > 1 && (
          <div className="ops-actions" style={{ justifyContent: "center" }}>
            <button className="ops-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span className="ops-muted" style={{ alignSelf: "center" }}>Page {page} of {pg.totalPages} · {pg.total} reservations</span>
            <button className="ops-btn" disabled={page >= pg.totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {open && <ReservationDrawer id={open} onClose={() => setOpen(null)} onChanged={reload} />}
      {newRes && <StaffBooking onClose={() => setNewRes(false)} onCreated={(r) => { setNewRes(false); reload(); setOpen(r.id); }} />}
    </AdminLayout>
  );
}
